/**
 * TelnyxRTC dressed as a Twilio Voice Device.
 *
 * TwilioDeviceContext is the product: 4k lines hardened against the Twilio SDK's exact event
 * semantics. When the voice token says `provider: 'telnyx'` it builds this where it would build
 * `new Device()` (the same swap demo mode makes with demo-voice-device), so every dialer runs the
 * same code on either carrier. Only the slice of the Twilio surface that file calls is here:
 *   Device: register / updateToken / connect / disconnectAll / destroy / state / calls / audio,
 *           events registered · unregistered · error · incoming · tokenWillExpire
 *   Call:   parameters.CallSid / accept / reject / disconnect / mute / isMuted / sendDigits /
 *           status / getLocalStream / _peerConnection,
 *           events accept · audio · volume · mute · error · cancel · reject · disconnect
 *
 * Twilio behaviours the context leans on, kept on purpose:
 *   - one terminal event per call (disconnect | cancel | reject); Telnyx reports hangup AND destroy
 *   - CallSid is the server's call_control_id (what the backend knows the leg by), never call.id
 *   - device.calls holds unanswered incoming calls only, and connect() drops them silently
 *   - no 'incoming' while a call is up (allowIncomingWhileBusy: false)
 *   - disconnect() does nothing to an unanswered incoming call; reject() only works on one
 *   - leaving the page hangs up (Twilio destroys its Device on pagehide). Otherwise the SDK
 *     re-attaches the old leg after a reload, next to the rejoin the context makes itself.
 */
import type { Call as TxCall, INotification, ITelnyxErrorEvent, ITelnyxWarningEvent, TelnyxRTC } from '@telnyx/webrtc';

type Fn = (...args: any[]) => void;

const TERMINAL = new Set(['hangup', 'destroy', 'purge']);
const TOKEN_EXPIRING_SOON = 34001;
// The connect params voice() reads; its DTO (VoiceWebhookValidator) drops the rest, and every extra
// SIP header (a lead's name, in any script) is one more reason for a hop to refuse the INVITE.
const HEADER_PARAMS = ['To', 'SelectedCallerId'];

const fault = (code: number, message: string) => Object.assign(new Error(message), { code });

/** How the far end ended the call → the code the context's error toast switch expects. */
function refusal(tx: TxCall) {
  const why = `${tx.sipReason ?? ''} ${tx.cause ?? ''}`.trim();
  const sip = Number(tx.sipCode) || 0;
  if (/\bD35\b/.test(why)) return fault(13214, why); // caller ID refused → "Phone Number Required"
  if (/\bD38\b/.test(why)) return fault(20003, why); // no outbound voice profile → "Service Unavailable"
  return sip >= 400 ? fault(31000 + sip, why || `SIP ${sip}`) : null; // Twilio's codes: 31000 + SIP
}

/** Per-call remote audio element, like the one Twilio hands out in its 'audio' event. */
function audioElement(sinkId: string | null): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null;
  const el = new Audio();
  if (sinkId && typeof el.setSinkId === 'function') el.setSinkId(sinkId).catch(() => {});
  return el;
}

class Emitter {
  private _listeners = new Map<string, Set<Fn>>();
  on(evt: string, fn: Fn) {
    if (!this._listeners.has(evt)) this._listeners.set(evt, new Set());
    this._listeners.get(evt)!.add(fn);
    return this;
  }
  off(evt: string, fn: Fn) {
    this._listeners.get(evt)?.delete(fn);
    return this;
  }
  removeListener(evt: string, fn: Fn) {
    return this.off(evt, fn);
  }
  removeAllListeners(evt?: string) {
    if (evt) this._listeners.delete(evt);
    else this._listeners.clear();
    return this;
  }
  protected emit(evt: string, ...args: any[]) {
    [...(this._listeners.get(evt) ?? [])].forEach((fn) => {
      try {
        fn(...args);
      } catch (err) {
        console.error(err);
      }
    });
  }
}

export class TelnyxCall extends Emitter {
  private _accepted = false; // incoming: accept() called
  private _answered = false; // 'accept' emitted
  private _closed = false;
  private _errored = false;
  private _replaced = new WeakSet<TxCall>();
  private _volume: { ctx: AudioContext; timer: ReturnType<typeof setInterval> } | null = null;

  constructor(
    private readonly _device: TelnyxDevice,
    public _tx: TxCall,
    private readonly _inbound: boolean,
    readonly _el: HTMLAudioElement | null,
  ) {
    super();
  }

  /** Twilio fills CallSid once the server rings/answers; incoming calls also carry From/To. */
  get parameters(): Record<string, string> {
    const params: Record<string, string> = {};
    const sid = this._tx.telnyxIDs?.telnyxCallControlId;
    if (sid) params.CallSid = sid;
    if (this._inbound) {
      const { remoteCallerNumber, destinationNumber } = this._tx.options;
      if (remoteCallerNumber) params.From = remoteCallerNumber;
      if (destinationNumber) params.To = destinationNumber;
    }
    return params;
  }

  get _peerConnection(): RTCPeerConnection | null {
    return this._tx.peer?.instance ?? null;
  }

  status() {
    if (this._closed) return 'closed';
    const state = this._tx.state;
    if (state === 'active' || state === 'held') return 'open';
    if (state === 'recovering') return 'reconnecting';
    if (this._inbound && !this._accepted) return 'pending';
    return state === 'ringing' || state === 'early' ? 'ringing' : 'connecting';
  }

  accept() {
    if (this._closed || !this._inbound || this._accepted) return;
    this._accepted = true;
    this._device._adopt(this);
    this._tx.answer({ remoteElement: this._el ?? undefined }).catch(() => {});
  }

  reject() {
    if (this._closed || !this._inbound || this._accepted) return;
    this._end();
    this._tx.hangup({ cause: 'USER_BUSY', causeCode: 17, sipCode: 486 }, true).catch(() => {});
    this.emit('reject');
  }

  disconnect() {
    if (this._closed || (this._inbound && !this._accepted)) return;
    this._tx.hangup().catch(() => {});
  }

  mute(shouldMute = true) {
    const was = this.isMuted();
    if (shouldMute) this._tx.muteAudio();
    else this._tx.unmuteAudio();
    if (was !== shouldMute) this.emit('mute', shouldMute, this);
  }

  isMuted() {
    return !!this._tx.isAudioMuted;
  }

  sendDigits(digits: string) {
    for (const digit of digits) this._tx.dtmf(digit);
  }

  getLocalStream() {
    return this._tx.localStream ?? null;
  }

  /** One SDK state change, already moved off the SDK's call stack by the device. */
  _onState(tx: TxCall, state: string) {
    if (this._closed || this._replaced.has(tx)) return;
    if (tx !== this._tx) {
      // the SDK re-created the call after a reconnect (same id, new object): keep going on the new one
      this._replaced.add(this._tx);
      this._tx = tx;
      if (this._answered) this._meter();
    }
    if (state === 'active' && !this._answered) {
      this._answered = true;
      this._meter();
      if (this._el) {
        this._el.play().catch(() => {});
        this.emit('audio', this._el);
      }
      this.emit('accept', this);
    } else if (TERMINAL.has(state)) {
      const unanswered = this._inbound && !this._accepted;
      const err = unanswered ? null : refusal(tx);
      this._end();
      if (err) this._error(err);
      if (unanswered) this.emit('cancel');
      else this.emit('disconnect', this);
    }
  }

  _error(err: Error) {
    if (this._errored) return;
    this._errored = true;
    this.emit('error', err);
  }

  _end() {
    this._closed = true;
    this._stopMeter();
    this._device._drop(this);
  }

  /** Twilio emits (input, output) volume ~20×/s; the context's ringback fallback waits on output. */
  private _meter() {
    this._stopMeter();
    const Ctx = typeof window === 'undefined' ? undefined : window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    try {
      const ctx: AudioContext = new Ctx();
      ctx.resume().catch(() => {});
      const tap = (stream?: MediaStream | null) => {
        if (!stream?.getAudioTracks().length) return null;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 32;
        ctx.createMediaStreamSource(stream).connect(analyser);
        return analyser;
      };
      const mic = tap(this._tx.localStream);
      const speaker = tap(this._tx.remoteStream);
      const bins = new Uint8Array(16);
      const level = (analyser: AnalyserNode | null) => {
        if (!analyser) return 0;
        analyser.getByteFrequencyData(bins);
        return bins.reduce((sum, v) => sum + v, 0) / bins.length / 255;
      };
      this._volume = { ctx, timer: setInterval(() => this.emit('volume', level(mic), level(speaker)), 50) };
    } catch {
      // no Web Audio: no volume events; the realtime customer_answered path still stops ringback
    }
  }

  private _stopMeter() {
    if (!this._volume) return;
    clearInterval(this._volume.timer);
    this._volume.ctx.close().catch(() => {});
    this._volume = null;
  }
}

export class TelnyxDevice extends Emitter {
  state: 'unregistered' | 'registering' | 'registered' | 'destroyed' = 'unregistered';
  private _client: TelnyxRTC;
  private _calls: TelnyxCall[] = []; // unanswered incoming, like Twilio's device.calls
  private _active: TelnyxCall | null = null;
  private _byId = new Map<string, TelnyxCall>();
  private _sinkId: string | null = null;
  private _reg: { promise: Promise<void>; settle: (err?: unknown) => void } | null = null;
  private _subs: Array<[string, Fn]> = [];

  readonly audio = {
    incoming: (_on?: boolean) => {}, // the context mutes Twilio's ringtone; this device never plays one
    setInputDevice: async (deviceId: string) => {
      await this._client.setAudioSettings({ micId: deviceId }); // next calls
      const tx = this._active?._tx;
      if (tx?.peer?.instance) await tx.setAudioInDevice(deviceId); // the live one
    },
    speakerDevices: {
      set: async (deviceId: string) => {
        this._sinkId = deviceId;
        const el = this._active?._el;
        if (el && typeof el.setSinkId === 'function') await el.setSinkId(deviceId);
      },
    },
  };

  constructor(token: string, RTC: typeof TelnyxRTC) {
    super();
    // hangupOnBeforeUnload would BYE the call when the "Leave site?" prompt opens, even if the
    // agent stays (CallSafeReloadGuard). pagehide below hangs up only when the page really goes.
    this._client = new RTC({ login_token: token, hangupOnBeforeUnload: false });
    const sub = (evt: string, fn: Fn) => {
      this._subs.push([evt, fn]);
      this._client.on(evt, fn);
    };
    // Everything the SDK reports is handled on a microtask: the context rejects/answers calls from
    // its handlers, which must not run inside the SDK's own state change (an invite would be
    // BYE'd before it was acked).
    sub('telnyx.ready', () => queueMicrotask(() => this._ready()));
    sub('telnyx.error', (ev: ITelnyxErrorEvent) => queueMicrotask(() => this._fail(ev)));
    sub('telnyx.warning', (ev: ITelnyxWarningEvent) => {
      if (ev?.warning?.code === TOKEN_EXPIRING_SOON) queueMicrotask(() => this.emit('tokenWillExpire', this));
    });
    const dropped = () =>
      queueMicrotask(() => {
        if (this.state !== 'registered') return;
        this.state = 'unregistered';
        this.emit('unregistered');
      });
    sub('telnyx.socket.close', dropped);
    sub('telnyx.socket.error', dropped);
    sub('telnyx.notification', (n: INotification) => {
      const tx = n?.type === 'callUpdate' ? n.call : undefined;
      if (!tx) return;
      const state = tx.state; // read now: the call moves on before the microtask runs
      queueMicrotask(() => this._onCall(tx, state));
    });
    if (typeof window !== 'undefined') window.addEventListener('pagehide', this._onPageHide);
  }

  get calls() {
    return this._calls;
  }

  register(): Promise<void> {
    if (this.state === 'destroyed') return Promise.reject(new Error('Device destroyed'));
    if (this.state === 'registered') return Promise.resolve();
    if (!this._reg) {
      let settle: (err?: unknown) => void = () => {};
      const promise = new Promise<void>((resolve, reject) => {
        settle = (err) => (err ? reject(err) : resolve());
      });
      this._reg = { promise, settle };
      this.state = 'registering';
      // an open socket (login refused, stale token) just logs in again; else connect, the SDK logs in on open
      if (this._client.connected) this._client.login().catch(() => {});
      else this._client.connect().catch((err) => this._settle(err ?? new Error('connect failed')));
    }
    return this._reg.promise;
  }

  updateToken(token: string) {
    this._client.options.login_token = token; // what the SDK's own reconnects log in with
    if (this.state === 'registered') this._client.login({ creds: { login_token: token } }).catch(() => {});
  }

  async connect({ params = {}, rtcConstraints }: { params?: Record<string, string>; rtcConstraints?: { audio?: MediaTrackConstraints } } = {}) {
    if (this.state === 'destroyed') throw new Error('Device destroyed');
    if (this._active) throw new Error('A Call is already active');
    this._calls.splice(0).forEach((call) => call._end()); // Twilio ignores unanswered incoming calls here, silently
    const el = audioElement(this._sinkId);
    const tx = this._client.newCall({
      destinationNumber: params.To, // E.164, or conference:/multiline:/predictive:<name> for the backend's TwiML
      // No callerNumber: the call is parked and our TwiML picks caller ID. A callerNumber would replace the
      // SIP From, the only thing that tells the backend which agent is dialing. X-SelectedCallerId carries it.
      customHeaders: HEADER_PARAMS.filter((name) => params[name] != null).map((name) => ({
        name: `X-${name}`,
        value: String(params[name]).replace(/[\r\n]+/g, ' '),
      })),
      audio: { ...rtcConstraints?.audio }, // copied: the SDK writes the chosen mic into it
      remoteElement: el ?? undefined,
    });
    const call = new TelnyxCall(this, tx, false, el);
    this._byId.set(tx.id, call);
    this._active = call;
    return call;
  }

  disconnectAll() {
    this._calls.splice(0).forEach((call) => call.disconnect()); // a no-op on unanswered calls, as in Twilio
    this._active?.disconnect();
  }

  destroy() {
    if (this.state === 'destroyed') return;
    this._calls.slice().forEach((call) => call.reject());
    this.disconnectAll();
    if (typeof window !== 'undefined') window.removeEventListener('pagehide', this._onPageHide);
    this._subs.forEach(([evt, fn]) => this._client.off(evt, fn));
    this._client.disconnect().catch(() => {});
    this.state = 'destroyed';
    this._settle(new Error('Device destroyed'));
    this.removeAllListeners();
  }

  /** An answered incoming call becomes the active one and leaves device.calls, as in Twilio. */
  _adopt(call: TelnyxCall) {
    this._calls = this._calls.filter((c) => c !== call);
    this._active = call;
  }

  _drop(call: TelnyxCall) {
    this._calls = this._calls.filter((c) => c !== call);
    if (this._active === call) this._active = null;
    if (this._byId.get(call._tx.id) === call) this._byId.delete(call._tx.id);
  }

  private _onPageHide = (e: PageTransitionEvent) => {
    if (e.persisted && !this._active && !this._calls.length) return; // bfcache, nothing live: keep it
    this.destroy();
  };

  private _ready() {
    if (this.state === 'destroyed') return;
    this.state = 'registered';
    this.emit('registered');
    this._settle();
  }

  private _fail(ev: ITelnyxErrorEvent) {
    const err = fault(ev?.error?.code ?? 0, ev?.error?.message ?? 'Dialer connection error');
    if (ev?.callId) {
      // one call's problem is that call's error, never the device's
      if (ev.error?.fatal !== false) this._byId.get(ev.callId)?._error(err);
      return;
    }
    if (this.state === 'destroyed') return;
    this.state = 'unregistered';
    this._settle(err);
    this.emit('error', err);
  }

  private _onCall(tx: TxCall, state: string) {
    const call = this._byId.get(tx.id);
    if (call) return call._onState(tx, state);
    if (TERMINAL.has(state) || this.state === 'destroyed') return;
    if (tx.recoveredCallId) {
      tx.hangup().catch(() => {}); // a leg re-attached from before a reload: the context rejoins on its own
    } else if ((tx.direction as string) === 'inbound' && state === 'ringing' && !this._active) {
      const incoming = new TelnyxCall(this, tx, true, audioElement(this._sinkId));
      this._byId.set(tx.id, incoming);
      this._calls.push(incoming);
      this.emit('incoming', incoming);
    }
  }

  private _settle(err?: unknown) {
    const reg = this._reg;
    this._reg = null;
    reg?.settle(err);
  }
}

/** Built by TwilioDeviceContext in place of `new Device(token)` when the voice token is Telnyx's. */
export async function createTelnyxDevice(token: string) {
  const { TelnyxRTC } = await import('@telnyx/webrtc'); // only Telnyx agents download the SDK
  return new TelnyxDevice(token, TelnyxRTC);
}
